import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Percent, Ruler, HandCoins, FilePdf, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const CommissionCard = ({ id, onCredits }) => {
  const [data, setData] = useState(null);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [applyOpen, setApplyOpen] = useState(false);
  const [openInv, setOpenInv] = useState([]);
  const [invId, setInvId] = useState("");
  const [applyAmt, setApplyAmt] = useState("");

  const emailStatement = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/customers/${id}/commissions/statement/email`, { month });
      toast.success(`Statement emailed to ${r.to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not email statement");
    }
    setBusy(false);
  };

  const downloadStatement = async () => {
    setBusy(true);
    try {
      const { data: blob } = await api.get(`/customers/${id}/commissions/statement`, { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error("Could not build statement");
    }
    setBusy(false);
  };

  const openApply = async () => {
    setInvId("");
    setApplyAmt("");
    setApplyOpen(true);
    try {
      const { data: rows } = await api.get(`/customers/${id}/commissions/open-invoices`);
      setOpenInv(rows || []);
    } catch {
      setOpenInv([]);
    }
  };

  const applyCredit = async () => {
    setBusy(true);
    try {
      const { data: next } = await api.post(`/customers/${id}/commissions/apply-credit`, { invoice_id: invId, amount: Number(applyAmt) });
      setData(next);
      onCredits?.(next.credits);
      setApplyOpen(false);
      toast.success("Credit applied to invoice");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not apply credit");
    }
    setBusy(false);
  };

  const load = () =>
    api.get(`/customers/${id}/commissions`).then(({ data }) => {
      setData(data);
      setS(data.settings);
      onCredits?.(data.credits);
    }).catch(() => {});
  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !s) return null;
  const dirty = JSON.stringify(s) !== JSON.stringify(data.settings);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/customers/${id}/commissions/settings`, { ...s, rate: Number(s.rate) || 0 });
      toast.success("Commission settings saved");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setBusy(false);
  };

  const voidEntry = async (eid) => {
    try {
      const { data } = await api.post(`/customers/${id}/commissions/${eid}/void`);
      setData(data);
      onCredits?.(data.credits);
      toast.success("Commission voided and credit reversed");
    } catch {
      toast.error("Could not void");
    }
  };

  const modeBtn = (v, Icon, label) => (
    <button
      type="button"
      data-testid={`commission-mode-${v}`}
      onClick={() => setS({ ...s, mode: v })}
      className={`inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-md border text-xs font-semibold ${
        s.mode === v ? "border-indigo-500/60 bg-indigo-500/15 text-indigo-200" : "border-zinc-800 text-zinc-400"
      }`}
    >
      <Icon size={13} weight="bold" /> {label}
    </button>
  );

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900" data-testid="profile-commission">
      <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-zinc-300">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-indigo-500/15 text-indigo-300">
            <HandCoins size={14} weight="bold" />
          </span>
          Commission
        </span>
        <label className="flex items-center gap-2 text-xs text-zinc-400">
          {s.enabled ? "On" : "Off"}
          <Switch data-testid="commission-enabled" checked={!!s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} />
        </label>
      </div>
      <div className="space-y-3 px-4 py-3">
        <div className="flex gap-2">
          {modeBtn("percent", Percent, "% of sale")}
          {modeBtn("per_sf", Ruler, "$ per sq ft")}
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="commission-rate"
            type="number"
            step="0.01"
            min="0"
            value={s.rate}
            onChange={(e) => setS({ ...s, rate: e.target.value })}
            className="h-9 w-28 border-zinc-800 bg-zinc-950 text-sm"
          />
          <span className="text-xs text-zinc-500">{s.mode === "per_sf" ? "per billed sq ft" : "% of subtotal after discounts"}</span>
        </div>
        <label className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-xs text-zinc-300">
          <span>
            Pay this organization
            <span className="block text-[10px] text-zinc-500">Adds commission as account credit when a quote is accepted</span>
          </span>
          <Switch data-testid="commission-pay-org" checked={!!s.pay_org} onCheckedChange={(v) => setS({ ...s, pay_org: v })} />
        </label>
        {dirty && (
          <Button data-testid="commission-save" onClick={save} disabled={busy} className="h-9 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest hover:bg-indigo-400">
            {busy ? "Saving…" : "Save commission settings"}
          </Button>
        )}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div className="rounded-md bg-zinc-950 px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Earned</div>
            <div className="font-mono-data text-sm font-bold text-zinc-100" data-testid="commission-earned">{money(data.earned)}</div>
          </div>
          <div className="rounded-md bg-zinc-950 px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Credited</div>
            <div className="font-mono-data text-sm font-bold text-emerald-300" data-testid="commission-credited">{money(data.credited)}</div>
          </div>
        </div>
        {data.shop?.statement_enabled && (
          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3" data-testid="commission-statement">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Monthly statement</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input data-testid="commission-statement-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40 border-zinc-800 bg-zinc-900 text-sm" />
              <Button
                data-testid="commission-statement-download"
                type="button"
                variant="outline"
                disabled={busy}
                onClick={downloadStatement}
                className="h-9 gap-1 border-zinc-700 text-xs"
              >
                <FilePdf size={14} weight="bold" /> PDF
              </Button>
              <Button data-testid="commission-statement-email" type="button" variant="outline" disabled={busy} onClick={emailStatement} className="h-9 gap-1 border-zinc-700 text-xs">
                <PaperPlaneTilt size={14} weight="bold" /> Email
              </Button>
            </div>
          </div>
        )}
        {data.shop?.apply_credit_enabled && data.credits > 0 && (
          <Button data-testid="commission-apply-open" type="button" variant="outline" onClick={openApply} className="h-9 w-full border-emerald-700/60 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/10">
            Apply {money(data.credits)} credit to an invoice
          </Button>
        )}
        {data.ledger.length > 0 && (
          <div className="divide-y divide-zinc-800/70">
            {data.ledger.slice(0, 8).map((e) => (
              <div key={e.id} data-testid={`commission-entry-${e.id}`} className={`flex items-center gap-2 py-2 text-xs ${e.status === "voided" ? "opacity-50 line-through" : ""}`}>
                <Link to={e.kind === "applied" ? `/invoices/${e.invoice_id}` : `/quotes/${e.quote_id}`} className="font-mono-data text-indigo-300 hover:underline">{e.kind === "applied" ? e.invoice_code : e.quote_code}</Link>
                <span className="min-w-0 flex-1 truncate text-zinc-400">
                  {e.kind === "applied" ? `Credit applied · ${e.customer_name}` : `${e.customer_name}${e.mode === "per_sf" ? ` · ${e.sqft} sf × $${e.rate}` : ` · ${e.rate}%`}`}
                </span>
                <span className={`font-mono-data font-bold ${e.kind === "applied" ? "text-amber-300" : "text-zinc-100"}`}>{e.kind === "applied" ? "-" : ""}{money(e.amount)}</span>
                {e.status === "credited" && e.kind !== "applied" && (
                  <button type="button" data-testid={`commission-void-${e.id}`} onClick={() => voidEntry(e.id)} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-red-300">
                    Void
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="border-zinc-800 bg-zinc-950 sm:max-w-md" data-testid="commission-apply-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Apply commission credit</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500">Available credit: <b className="text-emerald-300">{money(data.credits)}</b></p>
          {openInv.length === 0 ? (
            <p className="text-sm text-zinc-500" data-testid="commission-apply-empty">No open invoices for this organization or its people.</p>
          ) : (
            <div className="max-h-64 space-y-1 overflow-y-auto">
              {openInv.map((inv) => (
                <button
                  key={inv.id}
                  type="button"
                  data-testid={`commission-apply-invoice-${inv.id}`}
                  onClick={() => { setInvId(inv.id); setApplyAmt(Math.min(inv.balance, data.credits).toFixed(2)); }}
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-xs ${invId === inv.id ? "border-indigo-500/60 bg-indigo-500/10" : "border-zinc-800 bg-zinc-900"}`}
                >
                  <span><span className="font-mono-data text-indigo-300">{inv.code}</span> <span className="text-zinc-400">{inv.customer_name}</span></span>
                  <span className="font-mono-data text-zinc-100">{money(inv.balance)} due</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Input data-testid="commission-apply-amount" type="number" step="0.01" min="0" value={applyAmt} onChange={(e) => setApplyAmt(e.target.value)} className="h-9 w-32 border-zinc-800 bg-zinc-900 text-sm" />
            <Button data-testid="commission-apply-confirm" disabled={busy || !invId || !(Number(applyAmt) > 0)} onClick={applyCredit} className="h-9 flex-1 bg-emerald-600 text-xs font-bold uppercase tracking-widest hover:bg-emerald-500">
              {busy ? "Applying…" : "Apply credit"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
